import { useState, type ReactNode } from 'react';
import * as RD from '@radix-ui/react-dialog';
import { AlertTriangle, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button, type ButtonVariant } from './Button';

/**
 * Modal dialog. Radix handles focus trapping, Escape and scroll locking; the
 * styling here keeps it feeling like a panel that surfaced rather than a card
 * that floated in.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  width = 'md',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  width?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  const widths = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  } as const;

  return (
    <RD.Root open={open} onOpenChange={onOpenChange}>
      <RD.Portal>
        <RD.Overlay className="fixed inset-0 z-50 bg-black/55 backdrop-blur-[1px] data-[state=open]:animate-[fade-in_120ms_ease-out]" />
        <RD.Content
          className={cn(
            'fixed top-1/2 left-1/2 z-50 w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2',
            'border-line-strong bg-surface-raised flex max-h-[85vh] flex-col overflow-hidden rounded border',
            'shadow-2xl shadow-black/40 data-[state=open]:animate-[pop-in_140ms_var(--ease-out-quint)]',
            widths[width]
          )}
        >
          <div className="border-line flex shrink-0 items-start gap-3 border-b px-4 py-3">
            <div className="min-w-0 flex-1">
              <RD.Title className="text-ink text-base font-semibold tracking-tight">
                {title}
              </RD.Title>
              {description ? (
                <RD.Description className="text-ink-muted mt-1 text-xs leading-relaxed">
                  {description}
                </RD.Description>
              ) : null}
            </div>
            <RD.Close asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Close">
                <X className="size-3.5" />
              </Button>
            </RD.Close>
          </div>

          {children ? (
            <div className="scrollpane min-h-0 flex-1 px-4 py-3">{children}</div>
          ) : null}

          {footer ? (
            <div className="border-line bg-surface-sunken flex shrink-0 items-center justify-end gap-2 border-t px-4 py-2.5">
              {footer}
            </div>
          ) : null}
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}

/**
 * Confirmation for a destructive or money-moving action.
 *
 * `typeToConfirm` forces the operator to type a literal word before the button
 * arms. Reserve it for the genuinely irreversible — voiding a cart, reversing a
 * receipt, retiring a register — so it keeps its weight.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  variant = 'danger',
  typeToConfirm,
  consequences,
  loading = false,
  confirmDisabled = false,
  onConfirm,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  variant?: ButtonVariant;
  /** Word the operator must type exactly before confirming. */
  typeToConfirm?: string;
  /** Bullet list of what will actually happen. Be specific about money. */
  consequences?: string[];
  loading?: boolean;
  /**
   * Holds the confirm button while something on the screen behind the dialog is still open (say
   * why in `children`): that screen can change while the dialog is up, for example when a check
   * answers late.
   */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  children?: ReactNode;
}) {
  const [typed, setTyped] = useState('');
  const armed = (!typeToConfirm || typed.trim() === typeToConfirm) && !confirmDisabled;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setTyped('');
        onOpenChange(next);
      }}
      title={
        <span className="flex items-center gap-2">
          <AlertTriangle className="text-warn size-4" />
          {title}
        </span>
      }
      description={description}
      width="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant={variant}
            disabled={!armed}
            loading={loading}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {consequences && consequences.length > 0 ? (
        <ul className="border-warn/30 bg-warn-muted text-ink mb-3 space-y-1 rounded border p-2.5 text-xs">
          {consequences.map((c) => (
            <li key={c} className="flex gap-2">
              <span className="text-warn">•</span>
              <span>{c}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {children}

      {typeToConfirm ? (
        <label className="mt-1 block">
          <span className="text-2xs text-ink-faint tracking-wider uppercase">
            Type <span className="text-ink font-mono">{typeToConfirm}</span> to confirm
          </span>
          <input
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.currentTarget.value)}
            className="border-line bg-surface-sunken focus:border-signal focus:ring-signal mt-1 h-8 w-full rounded border px-2 font-mono text-sm focus:ring-1 focus:outline-none"
          />
        </label>
      ) : null}
    </Dialog>
  );
}
